import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Params } from '@angular/router'
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common'

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal'

import { take, takeUntil } from 'rxjs'
import { isEqual } from 'lodash-es'

import { ResponsiveComponent, SigninButtonComponent } from '@nx-superprep/shared'
import { User, UserService, ProviderId } from '@nx-superprep/backend/firebase'
import { isProduction, log } from '@nx-superprep/utils'

import { LegalComponent, LegalType } from '../legal'
import { FirebaseError } from '@angular/fire/app'
import { MatIconModule } from '@angular/material/icon'

@Component({
  standalone: true,
  selector: 'pop-ui-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  imports: [
    NgClass, NgStyle, NgTemplateOutlet,
    ReactiveFormsModule,
    MatIconModule,
    SigninButtonComponent
  ],
  providers: [BsModalService],
})
export class AuthComponent extends ResponsiveComponent implements OnInit, OnDestroy {

  get isProduction() { return isProduction() }

  @Input() get options() { return this._options; }
  set options(value) {
    this._options = value
    this._error = undefined
  }

  title = 'auth'
  masked = true

  get params() { return this._params; }

  isSubmitted = false
  guestForm!: FormGroup
  loginForm!: FormGroup
  registerForm!: FormGroup
  resetForm!: FormGroup
  forgotForm!: FormGroup

  hover = false

  private _submittedForm: FormGroup | undefined
  private _params!: Params

  private _options!: {
    template?: string,
    demo?: boolean,
    autologin?: boolean,
    tutorial?: boolean,
    state?: string,
    modal?: boolean
  }
  private _error: FirebaseError | undefined

  get step() { return this._step.toString(); }
  private _step = 1
  private _maxStep = 2

 readonly route = inject(ActivatedRoute)
 readonly formBuilder = inject(FormBuilder)
 readonly bsModalRef = inject(BsModalRef)
 readonly modalService = inject(BsModalService)
 readonly userService = inject(UserService)

  constructor() {
    super()
    this.rootClassName = 'auth-root-class-name'
  }

  override ngOnInit() {
    super.ngOnInit()
    const providerId = { providerId: ['', [Validators.nullValidator]] }
    const password = { password: ['', [Validators.required, Validators.minLength(1)]] }
    const email = { email: ['', [Validators.required, Validators.email]] }
    this.guestForm = this.formBuilder.group({
    })
    this.loginForm = this.formBuilder.group({
      ...providerId,
      email: ['', [Validators.required, Validators.nullValidator]],
      ...password
    })
    this.registerForm = this.formBuilder.group({
      ...providerId,
      firstName: ['', [Validators.nullValidator]],
      lastName: ['', [Validators.nullValidator]],
      profile: ['', [Validators.nullValidator]],
      ...email,
      ...password
    })
    this.resetForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    })
    this.forgotForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    })
    this.getData()
  }

  getData() {
    this.route.queryParams.pipe(takeUntil(this.onDestroy), take(1)).subscribe((params) => {
      this._params = params
    })
  }

  errorMessage(form: FormGroup) {
    return form.dirty ? this.userService.errorMessage(this._error) : undefined
  }

  error(form: FormGroup) {
    return form.dirty ? this._error : undefined
  }

  isInvalid(form: FormGroup, name: string) {
    return form.get(name)?.invalid && (form.get(name)?.dirty || form.get(name)?.touched)
  }

  isRequired(form: FormGroup, name: string) {
    return form.get(name)?.errors?.['required']
  }

  isClassRoom() {
    return this.classroomId?.code.length > 0
  }

  isClassRoomJoined() {
    return this.isClassRoom() && isEqual(this.classroomId, this.userService.classroomId)
  }

  isGuestAllowed() {
    return !this.isSubmitted && !this.userService.activeUser?.isAnonymous && !this.isClassRoom() && !this.error(this.registerForm)
  }

  getButtonLabel(form: FormGroup): string {
    const isSubmitted = this._submittedForm === form && this.isSubmitted

    if (form === this.loginForm) {
      if (isSubmitted) {
        switch (this.userService.status.value.step) {
          case 'auth': return 'Logged In'
          case 'post': return this.isClassRoom() ? 'Joining Classroom' : 'Starting Up'
          default: return 'Logging In'
        }
      }
      return 'Log In'
    }

    if (form === this.registerForm) {
      return isSubmitted ? 'Creating Account' : 'Sign Up For Free'
    }

    if (form === this.guestForm) {
      if (this.isClassRoom()) return 'Return to Log In'
      return isSubmitted ? 'Creating Account' : (this.isWeb ? 'Continue as a guest' : 'Guest Account')
    }

    if (form === this.resetForm) {
      return isSubmitted ? 'Submitting' : 'Submit'
    }

    return '' // Default case, if no match
  }

  // convenience getters for easy access to form fields
  get email() {
    switch(this.state) {
      case 'reset' : return this.resetForm.get('email')
      case 'register' : return this.registerForm.get('email')
      case 'login' :
      default : return this.loginForm.get('email')
    }
  }

  get password() {
    switch(this.state) {
      case 'register' : return this.registerForm.get('email')
      case 'login' :
      default : return this.loginForm.get('password')
    }
  }

  get profile() {
    return this.registerForm.get('profile')
  }

  get firstName() {
    return this.registerForm.get('firstName')
  }

  get lastName() {
    return this.registerForm.get('lastName')
  }

  onError(error: unknown) {
    log.debug(error)
    this._error = error as FirebaseError
    this.isSubmitted = false
    this._submittedForm = undefined
    this.masked = true
    this._step = 1
  }

  async onContinue(form: FormGroup) {
    switch (this.step.toString()) {
      case '1': if(form.controls['email'].invalid) { return; } break
      case '2': if(form.controls['password'].invalid) { return; } break
      default: return
    }
    this._step = Math.min(this._step + 1, this._maxStep)
    this._error = undefined
  }

  async onSubmit(form: FormGroup, providerId?: ProviderId) {
    if (!providerId && (this.isSubmitted || form.invalid)) { return }
    this._submittedForm = form
    this.isSubmitted = true
    this._error = undefined
    this.masked = true
    if (form.contains('providerId')) { form.controls['providerId'].setValue(providerId) }
    let user = this.userService.activeUser
    switch(form) {
      case this.guestForm: {
        if (user?.isAnonymous && !user.isSignup) { this.onClose(); return }
        try {
          user = await this.userService.signInWithClassroomId()
          this.onSignup(form, user)
        } catch (error) {
          this.onError(error)
        }
      } break
      case this.loginForm: {
        try {
          user = await this.userService.signInWithClassroomId({
            username: form.value.email,
            password: form.value.password,
            email: form.value.email,
            classroomId: this.classroomId,
            providerId
          })
          this.onSignup(form, user)
        } catch (error) {
          this.onError(error)
        }
      } break
      case this.registerForm: {
        try {
          if (user?.isAnonymous && !!user.password) {
            user = await this.userService.update(user, {
              username: form.value.email,
              password: form.value.password,
              email: form.value.email,
              currentPassword: user.password,
              firstName: form.value.firstName,
              lastName: form.value.lastName,
            })
          } else {
            user = await this.userService.signInWithClassroomId({
              register: true,
              username: form.value.email,
              password: form.value.password,
              email: form.value.email,
              firstName: form.value.firstName,
              lastName: form.value.lastName,
              classroomId: this.classroomId,
              providerId
            })
          }
          if (!user) { return; }
          this.onSignup(form, user)
      } catch (error) {
        this.onError(error)
      }
      } break
      case this.resetForm: {
        try {
          const found = await this.userService.exists(form.value.email)
          if (found) {
            const response = await this.userService.resetPassword(
              form.value.email,
            )
            log.debug(response)
            this.onClose()
          } else {
            this._error = new FirebaseError('auth/user-not-found', 'User does not exist.')
            this.isSubmitted = false
            this._submittedForm = undefined
          }
        } catch (error) {
          this.onError(error)
        }
      } break
      case this.forgotForm: {
        try {
          const response = await this.userService.forgotUsername(
            form.value.email,
          )
          log.debug(response)
        } catch (error) {
          this.onError(error)
        }
      } break
    }
  }

  // post authentication handler
  onSignup(form: FormGroup, user?: User) {
    const isUserCreation = user && !user.isConversion && [this.registerForm, this.guestForm].includes(form)
    const isClassRoomJoined = this.isClassRoomJoined()
    log.debug(user)
    this.onClose()
    if (user?.isConversion) { return }
    const route = !isClassRoomJoined && (isUserCreation || this.tutorial)
      ? { url: ['/get-started'], extras: {} }
      : { url: [this.isInFrame ? '/library' : '/home'], extras: {} }

    if (isClassRoomJoined) {
      // window.open(BackendService.host + route.url).focus()
      // window.top.location.replace(BackendService.host)
      //log.debug('postMessage', {message: 'ConfirmClassJoin'})
      //window.parent.postMessage({message: 'ConfirmClassJoin'}, '*')
    }
    this.router.navigate(route.url, {...route.extras, skipLocationChange: true })
  }

  get authStatus() { return this.userService.status }
  get isUserActive() { return this.userService.isUserActive; }
  get isUserAnonymous() { return this.userService.isAnonymous; }
  get isUserGuest() { return this.userService.isGuest; }

  // convenience getters for access to options fields
  get state() { return this.options.state; }
  set state(value) { this.options.state = value; }
  get tutorial() { return this.options.tutorial; }
  set tutorial(value) { this.options.tutorial = value; }
  get autologin() { return this.options.autologin; }
  get demo() { return this.options.demo; }

  // convenience getter for popteam parameters
  get classroomId() { return (this.params['code']??'').length > 0 ? { code:this.params['code'], partner: this.params['partner'] } : undefined }

  isLogin() {
    return this.state === 'login'
  }
  onLogin() {
    this._error = undefined
    const email =  this.email?.value
    this.state = 'login'
    this.email?.setValue(email)
  }
  onRegister() {
    this._error = undefined
    const email = this.email?.value
    this.state = 'register'
    this.email?.setValue(email)
  }
  onReset() {
    this._error = undefined
    const email = this.email?.value
    this.state = 'reset'
    this.email?.setValue(email)
  }

  onClose() {
    this.bsModalRef.hide()
    // Clean up state
    this._error = undefined
    this.isSubmitted = false
    this._submittedForm = undefined
    this._step = 1

    if (this._legalModalRef) {
      this._legalModalRef.hide()
    }
  }

  onDisplay(value?: string) {
    switch (value) {
      case 'privacy': this.legal = 'privacy'; break
      case 'terms': this.legal = 'tacos'; break
      default: break
    }
  }

  private get _bsModalLegalConfig(): ModalOptions {
    return {
      animated: true,
      keyboard: true,
      class: this.isWeb ? 'modal-lg':'modal-sm',
    }
  }
  private _legalModalRef?: BsModalRef<LegalComponent>
  private _legal: LegalType|undefined = undefined
  get legal() { return this._legal }
  set legal(value) {
    this._legal = value ? ['tacos','privacy'].includes(value) ? value : undefined : undefined
    if (value) {
      this._legalModalRef = this.modalService.show(LegalComponent, {...this._bsModalLegalConfig, initialState: {legal: this.legal}})
      this._legalModalRef.content?.legalChange.pipe(takeUntil(this.onDestroy)).subscribe((value: string) => this.legal = value as LegalType)
    } else {
      this._legalModalRef?.hide()
    }
  }
}
